import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { Payment as PaymentIcon, Launch as LaunchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { publicPaybosAPI } from '../services/paybosService';
import tripayService from '../services/tripayService';
import { BillForPaybosPayment, PaybosPaymentStatus } from '../types/paybos';
import QrisQrDisplay from '../components/QrisQrDisplay';
import PaybosFeeBreakdown from '../components/PaybosFeeBreakdown';

const PaybosPublicPaymentPage: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const [bill, setBill] = useState<BillForPaybosPayment | null>(null);
  const [payment, setPayment] = useState<PaybosPaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [gatewayEnabled, setGatewayEnabled] = useState(true);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });

  useEffect(() => {
    if (billId) {
      loadBill();
      checkExisting();
      tripayService.public.getGatewaysStatus().then((res) => {
        if (res.success) {
          setGatewayEnabled(!!res.data?.paybos?.enabled);
        }
      }).catch(() => setGatewayEnabled(false));
    }
  }, [billId]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);

  const loadBill = async () => {
    try {
      setLoading(true);
      const res = await publicPaybosAPI.getBillDetail(billId!);
      if (res.success) {
        setBill(res.data);
        setFormData({
          customer_name: res.data.customer?.name || '',
          customer_email: res.data.customer?.email || '',
          customer_phone: res.data.customer?.phone || '',
        });
        if (res.data.status === 'paid') setStep('payment');
      } else {
        setError(res.message || 'Tagihan tidak ditemukan');
      }
    } catch (e: any) {
      setError(e.message || 'Gagal memuat tagihan');
    } finally {
      setLoading(false);
    }
  };

  const buildPaymentStatus = async (data: Record<string, unknown>): Promise<PaybosPaymentStatus> => {
    let qrString = data.qr_string as string | undefined;
    const reference = (data.reference || data.paybos_id) as string;
    if (!qrString && reference) {
      const detail = await publicPaybosAPI.getPaymentDetail(reference);
      if (detail.success) qrString = detail.data.qr_string;
    }
    return {
      reference,
      merchant_ref: (data.merchant_ref || data.reference_id) as string,
      status: data.status as string,
      payment_method_name: 'QRIS PayBOS',
      amount: data.amount as number,
      bill_amount: data.bill_amount as number | undefined,
      service_fee: data.service_fee as number | undefined,
      expired_time: (data.expired_time || data.expired_at) as string,
      paid_time: data.paid_time as string | undefined,
      checkout_url: (data.checkout_url || data.payment_url) as string,
      qr_string: qrString,
    };
  };

  const checkExisting = async () => {
    try {
      const res = await publicPaybosAPI.checkExistingPayment(billId!);
      if (res.success && res.data && !res.data.is_expired && res.data.status === 'PENDING') {
        setPayment(await buildPaymentStatus(res.data));
        setStep('payment');
      } else if (res.data?.status === 'PAID') {
        setPayment(await buildPaymentStatus(res.data));
        setStep('payment');
      }
    } catch {
      // ignore
    }
  };

  const handleCreatePayment = async () => {
    if (!formData.customer_name || !formData.customer_email) {
      setError('Nama dan email wajib diisi');
      return;
    }
    try {
      setCreating(true);
      setError('');
      const res = await publicPaybosAPI.createPayment({
        bill_id: billId!,
        ...formData,
        replace_existing: step === 'payment',
      });
      if (res.success) {
        setPayment(await buildPaymentStatus(res.data));
        setStep('payment');
      } else {
        setError(res.message || 'Gagal membuat pembayaran');
      }
    } catch (e: any) {
      setError(e.message || 'Gagal membuat pembayaran');
    } finally {
      setCreating(false);
    }
  };

  const refreshStatus = async () => {
    if (!payment?.reference) return;
    const res = await publicPaybosAPI.checkPaymentStatus(payment.reference);
    if (res.success) setPayment(res.data);
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PaymentIcon color="primary" />
            <Typography variant="h5">Pembayaran Tagihan (PayBOS QRIS)</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!gatewayEnabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Pembayaran PayBOS sedang dinonaktifkan. Hubungi admin untuk informasi pembayaran.
            </Alert>
          )}

          {bill && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1">{bill.bill_number}</Typography>
              <Typography variant="body2" color="text.secondary">
                Periode: {bill.billing_period_start} — {bill.billing_period_end}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <PaybosFeeBreakdown
                  billAmount={bill.total_amount}
                  serviceFee={bill.service_fee || 0}
                  totalAmount={bill.payable_amount || bill.total_amount}
                  formatCurrency={formatCurrency}
                />
              </Box>
              <Chip
                label={bill.status === 'paid' ? 'LUNAS' : bill.status.toUpperCase()}
                color={bill.status === 'paid' ? 'success' : 'warning'}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          {bill?.status !== 'paid' && step === 'form' && gatewayEnabled && (
            <Stack spacing={2}>
              <TextField label="Nama" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} fullWidth required />
              <TextField label="Email" type="email" value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} fullWidth required />
              <TextField label="Telepon" value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} fullWidth />
              <Button variant="contained" size="large" onClick={handleCreatePayment} disabled={creating}>
                {creating ? <CircularProgress size={24} /> : 'Bayar via QRIS PayBOS'}
              </Button>
            </Stack>
          )}

          {payment && step === 'payment' && (
            <Stack spacing={2}>
              <Divider />
              <PaybosFeeBreakdown
                billAmount={payment.bill_amount ?? bill?.total_amount ?? payment.amount}
                serviceFee={payment.service_fee ?? bill?.service_fee ?? 0}
                totalAmount={payment.amount}
                formatCurrency={formatCurrency}
              />
              <Chip label={`Status: ${payment.status}`} color={payment.status === 'PAID' ? 'success' : 'warning'} />
              {payment.status === 'PENDING' && (
                <>
                  {payment.qr_string ? (
                    <QrisQrDisplay
                      qrString={payment.qr_string}
                      amount={payment.amount}
                      downloadFileName={`qris-${bill?.bill_number || payment.merchant_ref}.png`}
                    />
                  ) : (
                    <Alert severity="warning">
                      Kode QRIS belum tersedia. Gunakan tombol di bawah untuk membuka halaman pembayaran PayBOS.
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LaunchIcon />}
                    href={payment.checkout_url}
                    target="_blank"
                  >
                    Buka di Halaman PayBOS (alternatif)
                  </Button>
                  <Button variant="contained" startIcon={<RefreshIcon />} onClick={refreshStatus}>
                    Cek Status Pembayaran
                  </Button>
                </>
              )}
              {payment.status === 'PAID' && (
                <Alert severity="success">Pembayaran berhasil! Terima kasih.</Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaybosPublicPaymentPage;
